import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, MessageSquare } from 'lucide-react';
import { useApiClient } from '../hooks/useApiClient';
import { getPeerFeedbackDetail, respondToPeerFeedback } from '../utils/peerFeedbackApi';
import { PeerFeedbackDetail } from '../types/peerFeedback';
import { RichTextEditor } from '../components/common/RichTextEditor';
import { isRichTextEmpty } from '../utils/richText';
import { ActivityTimeline } from '../components/ActivityTimeline';

const QUESTIONS = [
  {
    key: 'startDoing' as const,
    label: 'What is one thing I should start, stop, and continue doing to make our collaboration more effective?',
    placeholder: 'Share specific behaviours or actions…',
  },
  {
    key: 'stopDoing' as const,
    label: "When I'm managing a high-pressure situation or a complex project, how does my leadership/working style affect the team's momentum?",
    placeholder: 'Be honest and constructive…',
  },
  {
    key: 'continueDoing' as const,
    label: 'What should I do more to help the team succeed?',
    placeholder: 'Highlight strengths worth keeping…',
  },
];

export function ProvideFeedbackPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { apiCall } = useApiClient();

  const [detail, setDetail] = useState<PeerFeedbackDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({ startDoing: '', stopDoing: '', continueDoing: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const requestId = Number(id);

  useEffect(() => {
    (async () => {
      try {
        const data = await getPeerFeedbackDetail(requestId, apiCall);
        setDetail(data);
        if (data?.status !== 'pending') setSubmitted(true);
      } catch {
        setError('Could not load feedback request.');
      } finally {
        setLoading(false);
      }
    })();
  }, [requestId, apiCall]);

  const answeredCount = QUESTIONS.filter((q) => !isRichTextEmpty(answers[q.key])).length;
  const progressPercent = (answeredCount / QUESTIONS.length) * 100;
  const allFilled = answeredCount === QUESTIONS.length;

  const handleSubmit = async () => {
    if (!allFilled) return;
    setSubmitting(true);
    setError(null);
    try {
      await respondToPeerFeedback(requestId, answers, apiCall);
      navigate('/feedback?tab=given');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-24 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!detail || error) {
    return (
      <div className="pt-24 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">{error ?? 'Request not found.'}</p>
          <button onClick={() => navigate('/feedback')} className="text-pink-600 hover:underline text-sm">
            ← Back to Feedback
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="pt-24 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-sm">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Already responded</h2>
          <p className="text-slate-500 text-sm mb-4">You have already submitted feedback for this request.</p>
          <button onClick={() => navigate('/feedback?tab=given')} className="text-pink-600 hover:underline text-sm">
            ← View Provided Feedback
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen bg-slate-50">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-40">
        <div className="h-1 bg-slate-200">
          <div
            className="h-full bg-gradient-to-r from-pink-400 to-purple-500 transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <main className="mx-auto px-10 lg:px-20 xl:px-32 py-8">
        {/* Back */}
        <button
          onClick={() => navigate('/feedback')}
          className="flex items-center gap-2 text-pink-500 hover:text-pink-600 transition-colors mb-6 font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Inbox
        </button>

        {/* Header banner */}
        <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl px-8 py-7 mb-8 shadow-xl text-white">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white mb-1">Provide 360 Feedback</h1>
              <p className="text-white/80 text-sm">
                Requested by <span className="font-semibold text-white">{detail.requesterName}</span>
              </p>
              {detail.message && (
                <div className="mt-3 bg-white/15 border border-white/20 rounded-xl px-4 py-3">
                  <p className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-1">Personal note</p>
                  <p className="text-sm text-white/90">{detail.message}</p>
                </div>
              )}
            </div>
            <div className="text-right flex-shrink-0">
              <div className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${
                allFilled
                  ? 'bg-green-400/20 text-green-100 border-green-300/30'
                  : 'bg-white/20 text-white border-white/30'
              }`}>
                {answeredCount}/{QUESTIONS.length} answered
              </div>
            </div>
          </div>
        </div>

        {/* Activity timeline */}
        <div className="mb-6">
          <ActivityTimeline events={detail.timeline} />
        </div>

        {/* Questions */}
        <div className="space-y-6">
          {QUESTIONS.map((q, index) => {
            const filled = !isRichTextEmpty(answers[q.key]);
            return (
              <div
                key={q.key}
                className={`bg-white rounded-2xl border shadow-sm p-6 transition-all ${
                  filled ? 'border-green-200' : 'border-slate-200'
                }`}
              >
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  <span className="text-pink-500 font-bold mr-2">{index + 1}.</span>
                  {q.label}
                </label>
                <RichTextEditor
                  value={answers[q.key]}
                  onChange={(val) => setAnswers((prev) => ({ ...prev, [q.key]: val }))}
                  placeholder={q.placeholder}
                  disabled={submitting}
                  minHeightClassName="min-h-[120px]"
                />
                {filled && (
                  <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" /> Answered
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-xl px-5 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* Footer actions */}
        <div className="mt-8 flex items-center justify-between pb-12">
          <button
            onClick={() => navigate('/feedback')}
            className="px-5 py-2.5 text-sm text-slate-600 hover:text-slate-800 border border-slate-300 bg-white rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!allFilled || submitting}
            className="inline-flex items-center gap-2 px-7 py-2.5 text-sm font-semibold bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 text-white rounded-xl transition-all shadow-sm"
          >
            <CheckCircle className="w-4 h-4" />
            {submitting ? 'Submitting…' : 'Submit Feedback'}
          </button>
        </div>
      </main>
    </div>
  );
}
